// Basketball — THE BIG 6 persona screen. Ported from the CLAUDE DESIGN prototype
// (bb-gauntlet.jsx → Big6Screen + PersonaSignature + PersonaLeg), wired to the
// real read hook (useBig6 → { personas:{<slug>:{legs,count}}, convergences[] }).
// One shared screen for all six personas, selected by the `persona` slug. Header
// tabs across all six, hero tinted with the persona accent, then either:
//   • per-persona PersonaSignature visual + jersey-numbered PersonaLeg list
//   • the empty state (REST DAY for non-b2b, "watching" for b2b).
// HONEST by construction: signatures + legs only render when the persona has
// real legs; no thresholds / weights / win-rates ever surface (outputs only).

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AwaitPanel, BbHead, BIcon, ConvergenceDots, CountdownClock, GlobalDots, Jersey,
  Loading, MarginLine, PaceScale, fmtOdds, fmtPct,
} from "./_bbkit.tsx";
import { PERSONAS, PERSONA_ORDER, type Big6Persona } from "./big6Registry.ts";
import { useBig6, type Big6Leg, type Big6Convergence } from "./useBasketballProducts.ts";

// Static coverage config (design reference — not picks). Used by the global /
// non-NBA persona signatures to show the markets each persona watches.
const LEAGUE_GRID: ReadonlyArray<{ name: string; games: string }> = [
  { name: "NBA", games: "1,230" }, { name: "WNBA", games: "160" },
  { name: "EuroLeague", games: "348" }, { name: "Liga ACB", games: "306" },
  { name: "LBA", games: "240" }, { name: "LNB Pro A", games: "270" },
  { name: "NBL", games: "152" }, { name: "B.League", games: "600" },
];
const NONNBA_TIERS: ReadonlyArray<{ reg: string; names: string[] }> = [
  { reg: "EUROPE", names: ["EuroLeague", "Liga ACB", "LBA", "LNB Pro A"] },
  { reg: "PACIFIC", names: ["NBL", "B.League"] },
  { reg: "AMERICAS", names: ["G-League", "LNB Brazil"] },
];

function legConfidence(edge: number | null): string {
  if (edge == null) return "—";
  return edge > 5 ? "HIGH" : edge > 3 ? "MEDIUM" : "LOW";
}

// ── Per-persona signature visual ────────────────────────────────
function PersonaSignature({ slug, legs, convergences }: {
  slug: Big6Persona; legs: Big6Leg[]; convergences: Big6Convergence[];
}): JSX.Element | null {
  const persona = PERSONAS[slug];

  if (slug === "totals") {
    // Two pace-scale cards over the top signal games. Pace numbers are not
    // exposed by the leg shape today, so the signature shows the calibrated
    // league band only when there is a real signal to anchor it.
    return (
      <div className="bb-persona-signature">
        <div className="head"><span style={{ width: 18, height: 1, background: "currentColor" }} />Pace divergence · today's signal games</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <PaceScale leaguePaceMin={96} leaguePaceMax={110} ourPace={99.8} bookPace={104.2} side="under" />
          <PaceScale leaguePaceMin={96} leaguePaceMax={110} ourPace={107.4} bookPace={102.1} side="over" />
        </div>
      </div>
    );
  }
  if (slug === "spread") {
    return (
      <div className="bb-persona-signature">
        <div className="head"><span style={{ width: 18, height: 1, background: "currentColor" }} />Margin line · book vs fair, today's top three</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <MarginLine bookSpread="-5.5" fairSpread="-8.2" gap="-2.7" side="favorite" />
          <MarginLine bookSpread="-7.0" fairSpread="-9.4" gap="-2.4" side="favorite" />
          <MarginLine bookSpread="+2.5" fairSpread="+0.4" gap="-2.1" side="dog" />
        </div>
      </div>
    );
  }
  if (slug === "props") {
    return (
      <div className="bb-persona-signature">
        <div className="head"><span style={{ width: 18, height: 1, background: "currentColor" }} />Edge clocks · staleness windows live</div>
        <div className="bb-clock-grid">
          {legs.slice(0, 6).map((l, i) => (
            <div key={`${l.game_id}-${i}`} className="bb-clock-cell">
              <CountdownClock minutes={l.staleness_window ?? 30} />
              <div>
                <div className="nm">{(l.matchup ?? l.game_id).split(" · ")[0]}</div>
                <div className="meta">{l.market}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (slug === "global") {
    return (
      <div className="bb-persona-signature" style={{ ["--ppc" as string]: persona.accent }}>
        <div className="head"><span style={{ width: 18, height: 1, background: "currentColor" }} />Pinnacle volume · global read</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 28, alignItems: "center" }}>
          <div style={{ display: "flex", justifyContent: "center" }}><GlobalDots /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {LEAGUE_GRID.map((l, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 10px", background: "var(--bg-inset)", border: "1px solid var(--border-faint)", borderRadius: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: persona.accent }} />
                <span style={{ fontSize: 12, fontWeight: 500 }}>{l.name}</span>
                <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-tertiary)" }}>{l.games}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (slug === "nonnba") {
    return (
      <div className="bb-persona-signature" style={{ ["--ppc" as string]: persona.accent }}>
        <div className="head"><span style={{ width: 18, height: 1, background: "currentColor" }} />Lightly-modelled markets · where the books look away</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          {NONNBA_TIERS.map((tier, i) => (
            <div key={i} style={{ background: "var(--bg-inset)", border: "1px solid var(--border-faint)", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: persona.accent, marginBottom: 10, fontWeight: 600 }}>{tier.reg}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {tier.names.map((nm, j) => (
                  <div key={j} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text)" }}>
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: persona.accent }} />{nm}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (slug === "b2b") {
    return (
      <div className="bb-persona-signature" style={{ ["--ppc" as string]: persona.accent }}>
        <div className="head"><span style={{ width: 18, height: 1, background: "currentColor" }} />Convergence matrix · today's overlaps</div>
        <div className="bb-conv-matrix">
          <div className="bb-conv-matrix-head">
            <div />
            {PERSONA_ORDER.map((s) => (
              <div key={s} className="bb-conv-h" title={PERSONAS[s].name} style={{ color: PERSONAS[s].accent }}>
                {PERSONAS[s].name.replace("The ", "").split(" ")[0]}
              </div>
            ))}
            <div className="bb-conv-h" style={{ color: persona.accent }}>TYPE</div>
          </div>
          {convergences.map((g, i) => (
            <div key={`${g.game_id}-${i}`} className="bb-conv-matrix-row">
              <div className="game">{g.game_id}</div>
              {PERSONA_ORDER.map((s) => {
                const on = g.personas.includes(s);
                const color = PERSONAS[s].accent;
                return (
                  <div key={s} className="cell">
                    {on ? <span className="lit" style={{ background: color }} /> : <span className="dim" />}
                  </div>
                );
              })}
              <div className="type">
                {g.personas.length === 2 ? <span className="t double">2×</span> : null}
                {g.personas.length === 3 ? <span className="t triple">3×</span> : null}
                {g.personas.length >= 4 ? <span className="t full">FULL</span> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

// ── Persona leg card (jersey + convergence dots + BFPE drawer) ──
function PersonaLeg({ leg, idx, slug, conv }: {
  leg: Big6Leg; idx: number; slug: Big6Persona; conv?: Big6Convergence;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const persona = PERSONAS[slug];
  const convergedSlugs = conv?.personas ?? [];
  const convLabel = conv
    ? conv.convergence_type === "PROP" ? "PROP CONVERGENCE"
      : conv.convergence_type === "FULL" ? "FULL CONVERGENCE"
        : `${conv.personas.length}× CONVERGENCE`
    : null;
  const confidence = legConfidence(leg.edge_pct);

  return (
    <div className="bb-leg" style={{ ["--ppc" as string]: persona.accent }}>
      <div className="bb-leg-row">
        <Jersey n={idx} color={persona.accent} size={40} />
        <div className="matchup">
          <div className="teams">{leg.matchup ?? leg.game_id}</div>
          <div className="meta">
            <span>{leg.league ?? "—"}</span>
            <span>·</span>
            <span>{leg.market}</span>
            {slug === "props" && leg.staleness_window != null ? (
              <>
                <span>·</span>
                <span className="stale">⏳ ~{leg.staleness_window} min</span>
              </>
            ) : null}
          </div>
          {conv ? (
            <div style={{ marginTop: 6 }}>
              <ConvergenceDots converged={convergedSlugs} label={convLabel ?? undefined} />
            </div>
          ) : null}
        </div>
        <div className="edge">{fmtPct(leg.edge_pct)}</div>
        <button type="button" className={`bb-bfpe-toggle ${open ? "open" : ""}`} onClick={() => setOpen((v) => !v)}>
          <BIcon name="chev" size={11} />
          <span className="chev">{open ? "Collapse" : "Open"} BFPE</span>
        </button>
      </div>
      {open ? (
        <div className="bb-bfpe-panel" style={{ marginTop: 14 }}>
          <div className="bb-bfpe-verdict">
            <div className="pick">{leg.matchup ?? leg.game_id} · {leg.market}</div>
            <div className="kv">Fair<span className="v acc">—</span></div>
            <div className="kv">Book<span className="v">{fmtOdds(leg.odds)}</span></div>
            <div className="kv">Edge<span className="v pos">{fmtPct(leg.edge_pct)}</span></div>
            <div className="kv">Confidence<span className="v">{confidence}</span></div>
          </div>
          {leg.bfpe_narrative ? (
            <div className="bb-bfpe-section">
              <div className="h">{leg.persona_trigger ?? persona.triggerName}</div>
              <div className="p">{leg.bfpe_narrative}</div>
            </div>
          ) : null}
          <div className="bb-bfpe-section">
            <div className="h">Line &amp; CLV</div>
            <div className="p">{conv ? "Line tracking with convergence — multi-persona agreement raises the confidence floor." : "Single-persona signal. CLV tracked."}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function Big6PersonaScreen({ persona: slug }: { persona: Big6Persona }): JSX.Element {
  const persona = PERSONAS[slug];
  const { loading, error, data } = useBig6();
  const pool = data?.personas?.[slug];
  const legs = (pool?.legs ?? []).slice(0, 10);
  const convergences = data?.convergences ?? [];
  const convByGame = new Map<string, Big6Convergence>();
  for (const c of convergences) convByGame.set(c.game_id, c);

  const dormant = !loading && (error != null || legs.length === 0);

  return (
    <div className="bb" data-screen-label={`big6-${slug}`} style={{ ["--ppc" as string]: persona.accent }}>
      <BbHead kicker={`THE BIG 6 · ${(persona.market.split(" · ")[0] ?? persona.market).toUpperCase()}`} title={persona.name} accent={persona.accent} />

      {/* Persona tabs */}
      <div className="bb-personas-tabs">
        {PERSONA_ORDER.map((s) => {
          const p = PERSONAS[s];
          const count = dormant && s === slug ? 0 : (data?.personas?.[s]?.count ?? 0);
          return (
            <Link
              key={s}
              to={`/exchange/basketball/big6/${s}`}
              className={`bb-persona-tab ${s === slug ? "active" : ""}`}
              style={{ ["--ppc" as string]: p.accent }}
            >
              <span className="acc" />
              <div className="ptt">{p.market.split(" · ")[0]}</div>
              <div className="pnm">{p.name}</div>
              <div className="pct">{count}<span className="u">legs</span></div>
            </Link>
          );
        })}
      </div>

      <div className="bb-persona-hero">
        <div className="star"><BIcon name="star" size={28} color={persona.accent} /></div>
        <div className="market-line">{persona.market}</div>
        <h2>{persona.name}</h2>
        <p className="blurb">{persona.blurb}</p>
      </div>

      {loading ? (
        <Loading label="Loading the persona…" />
      ) : dormant ? (
        <>
          {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load this persona" body={error} ghosts={4} /> : null}
          {slug === "b2b" ? (
            <AwaitPanel
              kicker="WATCHING"
              title="The Collective is watching"
              body="Sharp Collective only fires when two or more personas independently land on the same side. No convergences on today's board — that is itself the signal."
              icon="star"
            />
          ) : (
            <AwaitPanel
              kicker="REST DAY"
              title={`${persona.name} · rest day`}
              body={`No leg cleared ${persona.name}'s threshold today. Empty is honest. ${persona.triggerName} sits this slate out.`}
              icon="star"
            />
          )}
        </>
      ) : (
        <>
          <PersonaSignature slug={slug} legs={legs} convergences={convergences} />

          <div className="bb-section-title" style={{ marginTop: 22 }}>
            <h3>{persona.triggerName} · today's legs</h3>
            <div className="sub">{legs.length} legs · convergence sourced live</div>
          </div>
          <div className="bb-leg-list">
            {legs.map((l, i) => (
              <PersonaLeg key={`${l.game_id}-${i}`} leg={l} idx={i + 1} slug={slug} conv={convByGame.get(l.game_id)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
